import React from "react";
import { useDispatch, useSelector } from "react-redux";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import { Button, CircularProgress, Typography } from "@mui/material";
import {
  getPresignedUrl,
  uploadImageToS3,
  uploadImageToGMB,
  setUploadState,
  resetUploadState,
  selectUploadState,
  selectLoading,
} from "../../../Store/Gmb/gmbImageSlice";
import Dropdown from "./Dropdown";
import { validateGMBPhoto } from "../helper/validateGMBPhoto";
import { toast } from "react-toastify";

const IMAGE_OPTION_LIST = ["ADDITIONAL", "COVER", ""];

const ImageUploadModal = ({
  accountId,
  locationId,
  disabled,
  onImageUpdate,
}) => {
  const dispatch = useDispatch();
  const uploadState = useSelector(selectUploadState);
  const { upload: isImageUploading } = useSelector(selectLoading);

  const handleCategorySelected = (category) => {
    dispatch(setUploadState({ category }));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validation = await validateGMBPhoto(file);
    if (!validation.isValid) {
      toast.error(validation.error);
      event.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      dispatch(
        setUploadState({
          imagePreview: e.target.result,
          imageFile: file,
          isModalOpen: true,
        }),
      );
    };
    reader.readAsDataURL(file);
  };

  const handleClose = () => {
    dispatch(resetUploadState());
    const fileInput = document.getElementById("fileInput");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleUpload = async () => {
    const { imageFile, category } = uploadState;

    const validation = await validateGMBPhoto(imageFile, { category });
    if (!validation.isValid) {
      toast.error(validation.error);
      return;
    }

    const presignedUrlResponse = await dispatch(
      getPresignedUrl({ account_id: accountId, location_id: locationId }),
    ).unwrap();

    const s3UploadSuccess = await dispatch(
      uploadImageToS3({
        presignedUrl: presignedUrlResponse,
        file: imageFile,
      }),
    ).unwrap();

    if (s3UploadSuccess) {
      await dispatch(
        uploadImageToGMB({
          account_id: accountId,
          location_id: locationId,
          key: presignedUrlResponse?.fields?.key,
          category,
        }),
      ).unwrap();

      onImageUpdate();
      handleClose();
    }
  };

  const handleButtonClick = () => {
    dispatch(resetUploadState());
    document.getElementById("fileInput").click();
  };

  return (
    <div
      className={`relative  ${uploadState.isModalOpen ? "overflow-hidden" : ""}`}
    >
      <input
        type="file"
        id="fileInput"
        accept="image/*"
        onChange={handleImageUpload}
        className="hidden"
        disabled={disabled}
      />
      <AddCircleOutlineRoundedIcon
        onClick={handleButtonClick}
        sx={{
          color: disabled ? "#D3D3D3" : "#36454F",
          "&:hover": {
            color: disabled ? "#D3D3D3" : "#1e2a32",
            cursor: disabled ? "not-allowed" : "pointer",
          },
        }}
      />

      {uploadState.isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-[999999] bg-black  bg-opacity-50 backdrop-filter backdrop-blur-sm">
          <div className="relative p-2  mx-4 max-w-2xl bg-white rounded-lg shadow">
            <div className="flex items-center justify-between border-b p-2">
              <Typography variant="h3" className="text-xl font-semibold  ">
                Upload Image
              </Typography>
              <button
                onClick={handleClose}
                className="text-gray-400 text-2xl hover:bg-gray-200 px-2  mb-1 rounded-lg"
              >
                &times;
              </button>
            </div>

            <div className="px-2 rounded-lg">
              {uploadState.imagePreview && (
                <>
                  <img
                    src={uploadState.imagePreview}
                    alt="Preview"
                    className="w-[391px] object-cover rounded-lg mt-2 h-[240px]"
                  />
                  <div className="flex flex-row justify-start items-center mt-2 py-2 gap-2">
                    <Typography>Category:</Typography>
                    <Dropdown
                      defaultValue={uploadState.category}
                      onSelectedValue={handleCategorySelected}
                      optionList={IMAGE_OPTION_LIST}
                    />
                  </div>
                </>
              )}
            </div>

            <div className="flex items-center justify-start px-2 pt-2 border-t">
              <Button
                onClick={handleUpload}
                className="bg-[#EC221F] text-white px-4 py-2 rounded-lg hover:cursor-pointer hover:bg-[#FC101F]"
              >
                {isImageUploading ? (
                  <CircularProgress size="16px" color="inherit" />
                ) : (
                  "Upload"
                )}
              </Button>
              <Button
                onClick={handleClose}
                className="ml-3 border border-gray-200 px-4 py-2 rounded-lg hover:cursor-pointer hover:bg-blue-100"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUploadModal;
